// CASH1-S2-U1 (docs/build-briefs/CASH1-S2-U1-pay-screens.md; PRD P1.1; DEC-399): Settings > Location, this location's Cash switch. A
// structural twin of idVerifySetting.ts. Cash is a per-location setting a manager or admin turns on and off (owner ruling
// 2026-10-13, later). Off hides Cash at Pay and the drawer entry; turning it off with a drawer open never strands it.
//
// Over GET and PUT /tenants/:tenantId/locations/:locationId/cash-drawer-settings. The location is chosen from the locations the server
// lists (this device's location first), never typed. The switch acts over its own call and is NOT part of any Save; the change is
// audited by the server (Change history and the Audit log). After the call it draws what the server holds. Visible to a session
// that may view settings; the switch is off for one that may not manage them (resolved permissions, never a role name). Admin
// surface, every control 44px or taller. All text via textContent.

import {
  getCashDrawerSettings,
  listTenantLocations,
  putCashDrawerSettings,
  putCashEnabled,
  type CashDrawerSettingsDto,
  type TenantLocationNameDto,
} from "./api.ts";
import { sortByName } from "./nameSort.ts";
import { describeSetting } from "./settingsDescription.ts";
import {
  CASH_SETTING_LABELS,
  draftFromCents,
  parseCashSettingDraft,
  type CashSettingCents,
  type CashSettingDraft,
  type CashSettingErrors,
  type CashSettingField,
} from "./cashSettingModel.ts";

export interface CashSettingDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds admin.tenant_settings.view (the read of this section). */
  canView: () => boolean;
  /** The session holds admin.location_settings.manage (the save). */
  canManage: () => boolean;
  /** The location this device belongs to, when it is set up as one. */
  deviceLocation: () => { id: string; name: string } | undefined;
  /** True while the session this section serves is still the live one. */
  isCurrent: () => boolean;
}

export interface CashSettingSection {
  refresh(): Promise<void>;
  reset(): void;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

function messageOf(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "Something went wrong. Try again.";
}

const ID_PREFIX = "admin-cash";

export function mountCashSetting(root: HTMLElement, deps: CashSettingDeps): CashSettingSection {
  let locations: { id: string; name: string }[] = [];
  let locationId: string | undefined;
  let enabled: boolean | undefined;
  // CASH1-S5: what the server holds for the three figures, and what is typed in them.
  let held: CashSettingCents | undefined;
  let draft: CashSettingDraft | undefined;
  let fieldErrors: CashSettingErrors = {};
  let savedLine = "";
  let errorLine = "";
  let saving = false;
  let loadSequence = 0;
  root.hidden = true;

  function reset(): void {
    loadSequence += 1;
    locations = [];
    locationId = undefined;
    enabled = undefined;
    held = undefined;
    draft = undefined;
    fieldErrors = {};
    savedLine = "";
    errorLine = "";
    saving = false;
    root.replaceChildren();
    root.hidden = true;
  }

  async function loadLocations(): Promise<void> {
    const listed = await listTenantLocations(deps.apiBaseUrl(), deps.tenantId());
    const device = deps.deviceLocation();
    if (listed.status === 200 && Array.isArray(listed.body.locations)) {
      const found = listed.body.locations as TenantLocationNameDto[];
      locations = found.map((siteLocation) => ({ id: siteLocation.id, name: siteLocation.name }));
      const preferred =
        (device !== undefined &&
          locations.find((siteLocation) => siteLocation.id === device.id)?.id) ||
        found.find((siteLocation) => siteLocation.isMain)?.id ||
        locations[0]?.id;
      locationId =
        locationId !== undefined && locations.some((siteLocation) => siteLocation.id === locationId)
          ? locationId
          : preferred;
      return;
    }
    locations = device === undefined ? [] : [device];
    locationId = device?.id;
  }

  /** Takes what the server holds, figures included, and drops anything typed and unsaved. */
  function adopt(body: CashDrawerSettingsDto): void {
    enabled = body.cashEnabled;
    if (
      typeof body.standardFloatCents === "number" &&
      typeof body.toleranceCents === "number" &&
      typeof body.paidOutApprovalCents === "number"
    ) {
      held = {
        standardFloatCents: body.standardFloatCents,
        toleranceCents: body.toleranceCents,
        paidOutApprovalCents: body.paidOutApprovalCents,
      };
      draft = draftFromCents(held);
    } else {
      held = undefined;
      draft = undefined;
    }
    fieldErrors = {};
    savedLine = "";
  }

  async function load(): Promise<void> {
    if (!deps.canView()) {
      reset();
      return;
    }
    const sequence = (loadSequence += 1);
    if (locations.length === 0) {
      await loadLocations();
      if (sequence !== loadSequence || !deps.isCurrent()) return;
    }
    if (locationId === undefined) {
      reset();
      return;
    }
    const result = await getCashDrawerSettings(deps.apiBaseUrl(), deps.tenantId(), locationId);
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    if (result.status === 200 && typeof result.body.cashEnabled === "boolean") {
      adopt(result.body);
      render();
      return;
    }
    if (result.status === 403 || result.status === 404) {
      reset();
      return;
    }
    enabled = undefined;
    errorLine = `Could not load Cash. ${messageOf(result.body)}`;
    render();
  }

  function render(): void {
    root.replaceChildren();
    root.hidden = false;
    const card = node("section", "bd-card");
    card.dataset["testid"] = `${ID_PREFIX}-card`;
    const head = node("div", "bd-card-head");
    head.append(node("h3", "bd-card-title", "Cash"));
    card.append(head);

    if (locations.length > 1) {
      const chips = node("div", "rt-locations");
      chips.setAttribute("role", "group");
      chips.setAttribute("aria-label", "Which location");
      for (const siteLocation of sortByName(locations, (listed) => listed.name)) {
        const chip = node("button", "tablets-chip", siteLocation.name);
        chip.type = "button";
        chip.dataset["testid"] = `${ID_PREFIX}-location`;
        chip.dataset["locationId"] = siteLocation.id;
        chip.setAttribute("aria-pressed", String(siteLocation.id === locationId));
        chip.addEventListener("click", () => {
          if (siteLocation.id === locationId || saving) return;
          locationId = siteLocation.id;
          errorLine = "";
          void load();
        });
        chips.append(chip);
      }
      card.append(chips);
    }

    if (enabled !== undefined) {
      const row = node("div", "svc-row");
      const text = node("div", "svc-text");
      text.append(
        node("span", "svc-title", "Take cash"),
        node("span", "svc-sub", "Pay and drawers"),
      );
      const seg = node("div", "svc-seg svc-seg-two");
      seg.setAttribute("role", "radiogroup");
      seg.setAttribute("aria-label", "Take cash at this location");
      seg.dataset["testid"] = `${ID_PREFIX}-mode`;
      for (const option of [
        { key: false, label: "Off" },
        { key: true, label: "On" },
      ]) {
        const button = node("button", "svc-seg-btn", option.label);
        button.type = "button";
        button.setAttribute("role", "radio");
        button.setAttribute("aria-checked", String(option.key === enabled));
        button.dataset["testid"] = `${ID_PREFIX}-${option.key ? "on" : "off"}`;
        button.disabled = !deps.canManage() || saving;
        button.addEventListener("click", () => {
          if (option.key !== enabled) void save(option.key);
        });
        seg.append(button);
      }
      row.append(text, seg);
      card.append(row);
    }
    if (enabled === true && draft !== undefined) renderFigures(card, draft);
    if (errorLine !== "") {
      const error = node("p", "admin-error", errorLine);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = `${ID_PREFIX}-error`;
      card.append(error);
    }
    root.append(card);
  }

  const FIGURES: { field: CashSettingField; sub: string }[] = [
    { field: "float", sub: "Each shift opens with this" },
    { field: "tolerance", sub: "More than this needs a reason" },
    { field: "paidOut", sub: "At or above this amount" },
  ];

  function renderFigures(card: HTMLElement, working: CashSettingDraft): void {
    card.append(node("hr", "cash-setting-rule"));
    const mayManage = deps.canManage();
    for (const { field, sub } of FIGURES) {
      const row = node("div", "svc-row cash-setting-row");
      const text = node("div", "svc-text");
      const title = node("label", "svc-title", CASH_SETTING_LABELS[field]);
      const inputId = `${ID_PREFIX}-${field}`;
      title.htmlFor = inputId;
      const help = node("span", "svc-sub", sub);
      help.id = `${inputId}-help`;
      text.append(title, help);
      const affix = node("span", "field-input-affix field-input-affix--prefix cash-setting-field");
      const mark = node("span", "field-input-affix-mark", "$");
      mark.setAttribute("aria-hidden", "true");
      const input = node("input", "field-input cash-setting-input");
      input.type = "text";
      input.inputMode = "decimal";
      input.autocomplete = "off";
      input.id = inputId;
      input.dataset["testid"] = inputId;
      input.dataset["settingHelp"] = "limit";
      input.value = working[field];
      input.disabled = !mayManage || saving;
      describeSetting(input, `${inputId}-help`);
      if (fieldErrors[field] !== undefined) input.setAttribute("aria-invalid", "true");
      input.addEventListener("input", () => {
        working[field] = input.value;
        savedLine = "";
      });
      affix.append(input, mark);
      row.append(text, affix);
      card.append(row);
      const message = fieldErrors[field];
      if (message !== undefined) {
        const alert = node("p", "admin-error", message);
        alert.setAttribute("role", "alert");
        alert.dataset["testid"] = `${inputId}-error`;
        card.append(alert);
      }
    }
    const actions = node("div", "admin-settings-save-row");
    const saveButton = node("button", "btn-primary btn-compact", "Save");
    saveButton.type = "button";
    saveButton.dataset["testid"] = `${ID_PREFIX}-save`;
    saveButton.disabled = !mayManage || saving;
    saveButton.addEventListener("click", () => void saveFigures());
    const status = node("span", "admin-settings-save-status", savedLine);
    status.setAttribute("role", "status");
    status.dataset["testid"] = `${ID_PREFIX}-status`;
    actions.append(saveButton, status);
    card.append(actions);
  }

  async function saveFigures(): Promise<void> {
    if (locationId === undefined || saving || draft === undefined) return;
    const parsed = parseCashSettingDraft(draft);
    errorLine = "";
    savedLine = "";
    if (!parsed.ok) {
      fieldErrors = parsed.errors;
      render();
      return;
    }
    fieldErrors = {};
    const targetLocation = locationId;
    saving = true;
    render();
    const result = await putCashDrawerSettings(
      deps.apiBaseUrl(),
      deps.tenantId(),
      targetLocation,
      parsed.cents,
    );
    if (!deps.isCurrent()) return;
    saving = false;
    if (result.status !== 200 || typeof result.body.cashEnabled !== "boolean") {
      // Nothing was saved: keep what was typed and say why.
      errorLine = messageOf(result.body);
      render();
      return;
    }
    adopt(result.body);
    savedLine = "Saved";
    render();
  }

  async function save(next: boolean): Promise<void> {
    if (locationId === undefined || saving) return;
    const targetLocation = locationId;
    saving = true;
    errorLine = "";
    render();
    const result = await putCashEnabled(deps.apiBaseUrl(), deps.tenantId(), targetLocation, {
      cashEnabled: next,
    });
    if (!deps.isCurrent()) return;
    saving = false;
    if (result.status !== 200 || typeof result.body.cashEnabled !== "boolean") {
      // Nothing was saved: say why and show what the server holds.
      const message = messageOf(result.body);
      await load();
      errorLine = message;
      render();
      return;
    }
    // Draw what the server holds, not what was tapped.
    adopt(result.body);
    render();
  }

  return {
    async refresh() {
      errorLine = "";
      await load();
    },
    reset,
  };
}
